import { ReactElement, useMemo, useCallback } from 'react';
import { DialogTitle } from '@mui/material';
import { TabContext } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { includes } from 'lodash';

import { Nullable, TabPanelData } from '~globals/types/commons';

import { DialogAssingBillingListPanelsId } from './types';
import { useDialogAssingBillingListContext } from './DialogAssingBillingListContext';
import {
  DialogAssingBillingListEmptyState,
  DialogAssingBillingListSelection,
} from './Tabs';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import {
  DialogAssingBillingListContentContainer,
  DialogAssingBillingListTabPanel,
  DialogAssingBillingListActions,
  DialogAssingBillingListActionsButton,
  DialogAssingBillingListActionsButtonLoading,
} from './styles';

const TABS_PANELS: TabPanelData<DialogAssingBillingListPanelsId>[] = [
  {
    id: 'emptyState',
    panel: DialogAssingBillingListEmptyState,
  },
  {
    id: 'selection',
    panel: DialogAssingBillingListSelection,
  },
];

const DialogAssingBillingListContent = (): ReactElement => {
  const { activeTab, onClose, onConfirm, loadinConfirm } =
    useDialogAssingBillingListContext();

  const validateTabs = useCallback(
    (valids: DialogAssingBillingListPanelsId[]) => includes(valids, activeTab),
    [activeTab],
  );

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      let currentBillingListId: Nullable<string> =
        values[FIELDS_NAME.BILLING_LIST_ID];

      if (currentBillingListId === '_unassigned') {
        currentBillingListId = null;
      }

      await onConfirm(currentBillingListId);

      setSubmitting(false);
    },
    [onConfirm],
  );

  const formikBag = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnMount: true,
  });

  const { submitForm, isSubmitting, dirty, isValid } = formikBag;

  const loadingSubmit = useMemo(
    () => loadinConfirm || isSubmitting,
    [loadinConfirm, isSubmitting],
  );

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  return (
    <FormikProvider value={formikBag}>
      <DialogTitle>Asignar lista de precios</DialogTitle>

      <DialogAssingBillingListContentContainer dividers>
        <TabContext value={activeTab}>
          {TABS_PANELS.map(({ id, panel: Panel }) => (
            <DialogAssingBillingListTabPanel
              key={`dialog-assign-billing-list-panel-${id}`}
              value={id}
              sx={{ margin: validateTabs(['emptyState']) ? 'auto' : 0 }}
            >
              <Panel />
            </DialogAssingBillingListTabPanel>
          ))}
        </TabContext>
      </DialogAssingBillingListContentContainer>

      <DialogAssingBillingListActions>
        <DialogAssingBillingListActionsButton
          color="secondary"
          onClick={onClose}
        >
          Cerrar
        </DialogAssingBillingListActionsButton>

        {validateTabs(['selection']) && (
          <DialogAssingBillingListActionsButtonLoading
            variant="contained"
            color="primary"
            onClick={submitForm}
            loading={loadingSubmit}
            disabled={disabledSubmit}
          >
            Confirmar
          </DialogAssingBillingListActionsButtonLoading>
        )}
      </DialogAssingBillingListActions>
    </FormikProvider>
  );
};

export default DialogAssingBillingListContent;
